import React, { useEffect, useState } from 'react';
import axiosInstance from '../request';
import { URL } from '../api';
import { useNavigate } from 'react-router-dom';
import getPrivilegeList from "./PrivilegeList";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    interestRate: '',
    processFee: '',
    tenureInDays: '',
    numberOfEmi: ''
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(products.length / rowsPerPage);

  const privilegeList = getPrivilegeList();
  const navigate = useNavigate();

  const interestRateTypeMap = {
    1: 'Flat',
    2: 'Reducing'
  };

  const repaymentTypeMap = {
    1: 'Daily',
    2: 'Weekly',
    3: 'Monthly',
    4: 'Bullet',
    5: 'Quarterly',
    6: 'Bi Weekly',
    7: 'Yearly'
  };

  const productMappingMap = {
    1: 'Live Product',
    2: 'Agent Product',
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(URL.PRODUCT_LIST);
      if (Array.isArray(response.data) && response.data.length > 0) {
        setProducts(response.data);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching product list:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (product) => {
    setEditProduct(product);
    setFormData({
      interestRate: product.interestRate || '',
      processFee: product.processFee || '',
      tenureInDays: product.tenureInDays || '',
      numberOfEmi: product.numberOfEmi || '',
       platformCharge: formData.platformCharge || 0,
      collectionCharge: formData.collectionCharge || 0,
      convienceCharge: formData.convienceCharge || 0
    });
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateProduct = async () => {
    try {
      await axiosInstance.post(URL.PRODUCT_CREATE, {
        id: editProduct.id,
        interestRate: formData.interestRate || null,
        processFee: formData.processFee || null,
        tenureInDays: formData.tenureInDays || null,
        numberOfEmi: formData.numberOfEmi || null,
         platformCharge: formData.platformCharge || 0,
      collectionCharge: formData.collectionCharge || 0,
      convienceCharge: formData.convienceCharge || 0
      });
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  // Slice products for current page
  const paginatedProducts = products.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Product List</h3>
        {privilegeList.addProductView && (
          <button className="btn btn-primary" onClick={() => navigate('/addProduct')}>
            Add Product
          </button>
        )}
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle" style={{ whiteSpace: 'nowrap' }}>
          <thead className="table-dark">
            <tr>
              <th>S.No</th>
              <th>Product Id</th>
              <th>Product Name</th>
              <th>Interest Rate</th>
              <th>ROI</th>
              <th>Processing Fee</th>
              <th>Loan Amount</th>
              <th>Tenure(Days)</th>
              <th>Repayment Type</th>
              <th>Platform Charge</th>
              <th>Collection Charge</th>
              <th>Convience Charge</th>
              <th>Product Mapping</th>
              <th>No. of EMI</th>
              <th>Product Type</th>
              <th>Created Date</th>
              <th>Updated Date</th>
              {privilegeList.productEdit && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="15" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedProducts.length > 0 ? (
              paginatedProducts.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td>{item.id}</td>
                  <td>{item.productName}</td>
                  <td>{item.interestRate}%</td>
                  <td>{interestRateTypeMap[item.interestRateType] || item.interestRateType}</td>
                  <td>{item.processFee?.toLocaleString()}%</td>
                  <td>₹{item.loanAmount?.toLocaleString()}</td>
                  <td>{item.tenureInDays}</td>
                  <td>{repaymentTypeMap[item.repaymentType] || item.repaymentType}</td>
                  <td>{item.platformCharge ?? 0}%</td>
                  <td>{item.collectionCharge ?? 0}%</td>
                  <td>{item.convienceCharge ?? 0}%</td>
                  <td>{productMappingMap[item.mapping] || item.mapping}</td>
                  <td>{item.numberOfEmi}</td>
                  <td>{item.productType}</td>
                  <td>{item.createdDate ? new Date(item.createdDate).toLocaleDateString() : ''}</td>
                  <td>{item.updatedDate ? new Date(item.updatedDate).toLocaleDateString() : ''}</td>
                  {privilegeList.productEdit && (
                    <td>
                      <button className="btn btn-sm btn-warning me-2" onClick={() => handleEditClick(item)}>
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="15" className="text-center text-muted">
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {products.length > rowsPerPage && (
        <div className="d-flex justify-content-center mt-3">
          <div className="d-flex align-items-center overflow-auto" style={{ gap: '5px' }}>
            {/* Prev */}
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {/* Page numbers */}
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`btn btn-sm ${currentPage === i + 1 ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            {/* Next */}
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editProduct && (
        <div className="modal show fade" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Product (ID: {editProduct.id})</h5>
                <button type="button" className="btn-close" onClick={() => setEditProduct(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label>Interest Rate</label>
                  <input
                    type="number"
                    className="form-control"
                    name="interestRate"
                    value={formData.interestRate}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="mb-3">
                  <label>Processing Fee (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="processFee"
                    value={formData.processFee}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="mb-3">
                  <label>Tenure (Days)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="tenureInDays"
                    value={formData.tenureInDays}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="mb-3">
                  <label>No. of EMI</label>
                  <input
                    type="number"
                    className="form-control"
                    name="numberOfEmi"
                    value={formData.numberOfEmi}
                    onChange={handleFormChange}
                  />
                </div>

                
          <div className="mb-3">
            <label>Platform Charge (%)</label>
            <input
              type="number"
              className="form-control"
              name="platformCharge"
              value={formData.platformCharge ?? 0}
              onChange={handleFormChange}
            />
          </div>
          <div className="mb-3">
            <label>Collection Charge (%)</label>
            <input
              type="number"
              className="form-control"
              name="collectionCharge"
              value={formData.collectionCharge ?? 0}
              onChange={handleFormChange}
            />
          </div>
          <div className="mb-3">
            <label>Convenience Charge (%)</label>
            <input
              type="number"
              className="form-control"
              name="convienceCharge"
              value={formData.convienceCharge ?? 0}
              onChange={handleFormChange}
            />
          </div>

              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setEditProduct(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleUpdateProduct}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
